import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // --- Light/Dark Mode Logic ---
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        const bodyTheme = document.body.getAttribute('data-theme-version');
        return (savedTheme && ['light', 'dark'].includes(savedTheme)) ? savedTheme :
            (bodyTheme && ['light', 'dark'].includes(bodyTheme)) ? bodyTheme : 'light';
    });

    useEffect(() => {
        document.body.setAttribute('data-theme-version', theme);
        // Also sync with global template settings if they exist
        if (window.dezSettingsOptions) {
            window.dezSettingsOptions.version = theme;
        }
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Sync state if external script changes body attribute
    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'data-theme-version') {
                    const newTheme = document.body.getAttribute('data-theme-version');
                    if (newTheme && (newTheme === 'light' || newTheme === 'dark') && newTheme !== theme) {
                        setTheme(newTheme);
                    }
                }
            });
        });

        observer.observe(document.body, { attributes: true });
        return () => observer.disconnect();
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    // --- Primary Color Logic ---
    const DEFAULT_COLOR = '#533B9C'; // The requested purple
    const [primaryColor, setPrimaryColor] = useState(() => {
        return localStorage.getItem('primaryColor') || DEFAULT_COLOR;
    });

    // Helper to convert hex to rgb (for --bs-primary-rgb if needed)
    const hexToRgb = (hex) => {
        // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b) => {
            return r + r + g + g + b + b;
        });

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ?
            `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
            : '83, 59, 156'; // fallback
    };

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--primary', primaryColor);
        // Also update standard bootstrap variables if consistent styling is desired
        root.style.setProperty('--bs-primary', primaryColor);
        root.style.setProperty('--bs-primary-rgb', hexToRgb(primaryColor));

        localStorage.setItem('primaryColor', primaryColor);
    }, [primaryColor]);

    const changePrimaryColor = (color) => {
        setPrimaryColor(color);
    };

    // --- Table Zoom Logic ---
    const [tableZoom, setTableZoom] = useState(() => {
        const savedZoom = localStorage.getItem('tableZoom');
        return savedZoom ? parseFloat(savedZoom) : 1.0;
    });

    useEffect(() => {
        document.documentElement.style.setProperty('--table-font-scale', tableZoom);
        localStorage.setItem('tableZoom', tableZoom);
    }, [tableZoom]);

    const increaseTableZoom = () => {
        setTableZoom(prev => Math.min(prev + 0.1, 1.5)); // Max 150%
    };

    const decreaseTableZoom = () => {
        setTableZoom(prev => Math.max(prev - 0.1, 0.7)); // Min 70%
    };

    // --- Auto-Hide Header Logic ---
    const [autoHideHeader, setAutoHideHeader] = useState(() => {
        const saved = localStorage.getItem('autoHideHeader');
        return saved === 'true'; // Default to false
    });

    useEffect(() => {
        // Sync to body attribute
        document.body.setAttribute('data-auto-hide-header', autoHideHeader.toString());
        localStorage.setItem('autoHideHeader', autoHideHeader.toString());
    }, [autoHideHeader]);

    const toggleAutoHideHeader = () => {
        setAutoHideHeader(prev => !prev);
    };

    const value = {
        theme,
        toggleTheme,
        isDarkMode: theme === 'dark',
        primaryColor,
        changePrimaryColor,
        tableZoom,
        increaseTableZoom,
        decreaseTableZoom,
        autoHideHeader,
        toggleAutoHideHeader
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
